import React, { useState } from "react";
import axios from "axios";
import { Plus, Trash2 } from "lucide-react";

const AdminDocumentUploader = ({ type, bookingId, onUploadSuccess }) => {
  const [rows, setRows] = useState([{ id: Date.now(), files: [], remark: "" }]);
  const [isUploading, setIsUploading] = useState(false);

  const addRow = () => {
    setRows([...rows, { id: Date.now(), files: [], remark: "" }]);
  };

  const removeRow = (id) => {
    setRows(rows.filter((row) => row.id !== id));
  };

  const handleFileChange = (id, e) => {
    const updatedFiles = Array.from(e.target.files);
    setRows(
      rows.map((row) => (row.id === id ? { ...row, files: updatedFiles } : row))
    );
  };

  const handleRemarkChange = (id, value) => {
    setRows(
      rows.map((row) => (row.id === id ? { ...row, remark: value } : row))
    );
  };

  const handleUploadAll = async () => {
    const rowsToUpload = rows.filter((row) => row.files.length > 0);
    if (rowsToUpload.length === 0) {
      alert("Please select files for at least one row.");
      return;
    }

    setIsUploading(true);
    const base = import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || "http://localhost:3000";
    let finalDocs = [];

    try {
      for (const row of rowsToUpload) {
        const formData = new FormData();
        row.files.forEach((file) => formData.append("files", file));
        formData.append("remark", row.remark);

        const res = await axios.post(
          `${base}/api/admin-documents/${type}/${bookingId}`,
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          }
        );

        if (res.data.success) {
          finalDocs = res.data.data; // Store the updated array from the latest call
        }
      }

      alert("Documents uploaded successfully!");
      setRows([{ id: Date.now(), files: [], remark: "" }]);
      if (onUploadSuccess) onUploadSuccess(finalDocs);
    } catch (error) {
      console.error(error);
      alert("Error uploading documents: " + (error.response?.data?.error || error.message));
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="mt-4 p-4 border rounded bg-gray-50">
      <h3 className="font-semibold mb-4 text-lg">Upload Documents</h3>
      
      <div className="space-y-4">
        {rows.map((row, index) => (
          <div key={row.id} className="p-4 bg-white border rounded shadow-sm relative">
            {rows.length > 1 && (
              <button
                onClick={() => removeRow(row.id)}
                className="absolute top-3 right-3 text-red-500 hover:text-red-700 transition"
                title="Remove Row"
              >
                <Trash2 size={18} />
              </button>
            )}
            <h4 className="text-sm font-medium text-gray-700 mb-2">Document Group {index + 1}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-gray-500 mb-1">Select Files (Multiple allowed)</label>
                <input
                  type="file"
                  multiple
                  onChange={(e) => handleFileChange(row.id, e)}
                  className="block w-full text-sm text-gray-600
                    file:mr-3 file:py-1.5 file:px-3
                    file:rounded-md file:border-0
                    file:text-xs file:font-semibold
                    file:bg-blue-50 file:text-blue-700
                    hover:file:bg-blue-100 cursor-pointer"
                />
              </div>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Remark (Optional)</label>
                <input
                  type="text"
                  value={row.remark}
                  onChange={(e) => handleRemarkChange(row.id, e.target.value)}
                  className="w-full border p-1.5 text-sm rounded focus:ring-1 focus:ring-blue-400 focus:outline-none"
                  placeholder="E.g., Passport copies, E-Ticket..."
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mt-4">
        <button
          onClick={addRow}
          type="button"
          className="flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 transition"
        >
          <Plus size={16} /> Add Another Document
        </button>

        <button
          onClick={handleUploadAll}
          disabled={isUploading || rows.every(r => r.files.length === 0)}
          className="bg-blue-600 text-white py-2 px-6 rounded font-medium hover:bg-blue-700 disabled:opacity-50 transition w-full sm:w-auto"
        >
          {isUploading ? "Uploading..." : "Upload All Documents"}
        </button>
      </div>
    </div>
  );
};

export default AdminDocumentUploader;
